import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import PageHero from "./PageHero";
import { Reveal, SectionEyebrow, ArrowIcon } from "../components/common/Section";
import { useI18n } from "../lib/i18n";
import { resolveImage } from "../components/admin/ImageUpload";
import api from "../lib/api";

export const BusinessesListPage = () => {
  const { t, pick } = useI18n();
  const [units, setUnits] = useState([]);

  useEffect(() => {
    api.get("/public/business-units").then((r) => setUnits(r.data));
  }, []);

  return (
    <div data-testid="businesses-list-page">
      <PageHero
        eyebrow={t("nav.businesses")}
        title={{ tr: "Faaliyet Alanlarımız", en: "Our Businesses" }}
        subtitle={{
          tr: "Çok sektörlü portföyümüz; inşaat, gayrimenkul, yatırım, turizm ve teknoloji alanlarında kalıcı değer üretiyor.",
          en: "Our diversified portfolio creates enduring value across construction, real estate, investment, hospitality and technology.",
        }}
        image="https://images.unsplash.com/photo-1481026469463-66327c86e544"
      />
      <section className="bg-white">
        <div className="container-x py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
            {units.map((u, i) => (
              <motion.div
                key={u.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.06, ease: [0.19, 1, 0.22, 1] }}
                data-testid={`bu-tile-${u.slug}`}
              >
                <Link to={`/businesses/${u.slug}`} className="zoom-parent block group">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={resolveImage(u.cover_image)} alt={pick(u.name, "")} className="w-full h-full object-cover zoom-img" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/80 to-transparent" />
                    <div className="absolute inset-0 p-8 flex flex-col justify-end text-white">
                      <div className="text-[0.66rem] tracking-[0.28em] uppercase text-white/70">0{i + 1}</div>
                      <div className="font-serif text-3xl md:text-4xl mt-3">{pick(u.name, "")}</div>
                    </div>
                  </div>
                  <div className="mt-6 flex items-start justify-between gap-6">
                    <p className="text-brand-medium max-w-md">{pick(u.description, "")}</p>
                    <div className="link-gold shrink-0 mt-2">
                      <span>{t("common.readMore")}</span>
                      <ArrowIcon />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export const BusinessDetailPage = () => {
  const { slug } = useParams();
  const { t, pick } = useI18n();
  const [bu, setBu] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get(`/public/business-units/${slug}`).then((r) => setBu(r.data)).catch(() => setBu(null));
    api.get(`/public/projects?business_unit=${slug}`).then((r) => setProjects(r.data));
  }, [slug]);

  if (!bu) return <div className="container-x pt-40 pb-20">…</div>;

  return (
    <div data-testid="business-detail-page">
      <PageHero eyebrow={t("nav.businesses")} title={bu.name} subtitle={bu.tagline} image={resolveImage(bu.cover_image)} />
      <section className="bg-white">
        <div className="container-x py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionEyebrow>{pick(bu.name, "")}</SectionEyebrow>
              <h2 className="display display-md mt-6">{pick(bu.tagline, "")}</h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <p className="text-brand-dark text-lg leading-relaxed">{pick(bu.description, "")}</p>
              <p className="mt-6 text-brand-medium leading-relaxed">{pick(bu.long_description, "")}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {projects.length ? (
        <section className="bg-brand-light">
          <div className="container-x py-24 md:py-32">
            <Reveal>
              <SectionEyebrow>{t("common.projects")}</SectionEyebrow>
              <h2 className="display display-md mt-6">{pick({ tr: "Bu alandaki projeler", en: "Projects in this business" }, "")}</h2>
            </Reveal>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
              {projects.slice(0, 6).map((p) => (
                <Link key={p.id} to={`/projects/${p.slug}`} className="zoom-parent block group" data-testid={`bu-project-${p.slug}`}>
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={resolveImage(p.cover_image)} alt={pick(p.name, "")} className="w-full h-full object-cover zoom-img" loading="lazy" />
                  </div>
                  <div className="mt-4">
                    <div className="text-[0.66rem] tracking-[0.28em] uppercase text-brand-medium">{pick(p.location, "")}</div>
                    <div className="mt-2 font-serif text-xl text-brand-navy">{pick(p.name, "")}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
};
